import { Controller } from "stimulus";
import consumer from "channels/consumer";
import { formatMoney } from "utils/money";
import { applyAvatar } from "utils/avatar";

// Matches only http(s):// URLs (bare domains and javascript:/data:/mailto: never match). XSS-safety
// comes from the DOM build path (createElement + setAttribute + textContent, never innerHTML) and the
// forced https?:// scheme; the "<" exclusion is defense-in-depth. Flags are applied per use in fillBody.
const CHAT_URL_REGEX = /https?:\/\/[^\s<]+/;

export default class ChatPanel extends Controller {
  static targets = [
    "messages", "input", "submit", "banned", "hint", "deleteIcon", "banIcon", "moderatorBadge",
    "announcement", "announcementBody", "announcementText", "announcementToggle",
    "messageTemplate", "tipMessageTemplate", "moderatorActionsTemplate", "tipModeratorActionsTemplate",
  ];
  static values = {
    currentUserId: Number,
    moderator: Boolean,
    contextType: String,
    contextId: Number,
    heartbeatIntervalMs: Number,
    subscriberProfileBaseUrl: String,
  };

  connect() {
    this.banned = false;
    this.announcementExpanded = false;
    // Ids of messages we sent, so their echo back over the cable is recognized as our own (see
    // received()'s "message" branch) rather than treated like any other viewer's message.
    this.sentClientMsgIds = new Set();
    // Tracks whether the reader was at the bottom of the list, kept current via the scroll
    // listener below. Initialized eagerly (not left undefined) so the very first keyboard-open
    // resize on mobile already has a valid value to re-pin against.
    this.pinnedToBottom = true;

    // Stimulus singular target getters are not memoized (each access re-queries the
    // controller's subtree, which only grows as messages accumulate for the session), so
    // resolve these once and reuse the cached elements from the per-message build methods.
    this.messageTemplateEl = this.messageTemplateTarget;
    this.tipMessageTemplateEl = this.tipMessageTemplateTarget;
    if (this.hasModeratorActionsTemplateTarget) this.moderatorActionsTemplateEl = this.moderatorActionsTemplateTarget;
    if (this.hasTipModeratorActionsTemplateTarget) this.tipModeratorActionsTemplateEl = this.tipModeratorActionsTemplateTarget;

    this.subscription = consumer.subscriptions.create(
      {
        channel: "LiveStreamChannel",
        context_type: this.contextTypeValue,
        context_id: this.contextIdValue,
      },
      {
        received: this.received.bind(this),
        connected: this.onConnected.bind(this),
        disconnected: this.onDisconnected.bind(this),
      }
    );

    // The announcement chevron only appears when the 2-line-clamped text overflows;
    // re-check on resize because the chat panel width changes across breakpoints.
    this.onResize = () => this.scheduleAnnouncementToggleRefresh();
    window.addEventListener("resize", this.onResize);
    this.scheduleAnnouncementToggleRefresh();

    // Keep pinnedToBottom current as the reader scrolls, so a later resize (e.g. the mobile
    // keyboard opening/closing) knows whether it should re-pin.
    this.onMessagesScroll = () => { this.pinnedToBottom = this.isPinnedToBottom(); };
    this.messagesTarget.addEventListener("scroll", this.onMessagesScroll, { passive: true });

    // The visual viewport (not window) resizes when the on-screen keyboard opens/closes on
    // mobile; re-pin to the bottom across that resize so a pinned reader never loses their place.
    this.onViewportResize = () => {
      if (this.pinnedToBottom) {
        window.requestAnimationFrame(() => {
          if (this.hasMessagesTarget) this.scrollToBottom();
        });
      }
    };
    if (window.visualViewport) window.visualViewport.addEventListener("resize", this.onViewportResize);

    // Keep the server-side presence entry fresh. The interval comes from
    // LiveViewerPresence::HEARTBEAT_INTERVAL and must stay well under the server's
    // PRESENCE_TTL (45s) so a tab is never evicted while open. The immediate ping
    // fires from onConnected (once the channel is actually up).
    this.heartbeat = window.setInterval(() => this.subscription.perform("heartbeat"), this.heartbeatIntervalMsValue);

    // Re-register promptly after the browser throttles background timers.
    this.onVisibilityChange = () => {
      if (document.visibilityState === "visible") this.subscription.perform("heartbeat");
    };
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  disconnect() {
    if (this.subscription) {
      consumer.subscriptions.remove(this.subscription);
      this.subscription = null;
    }
    if (this.heartbeat) window.clearInterval(this.heartbeat);
    if (this.hintTimeout) window.clearTimeout(this.hintTimeout);
    if (this.onResize) window.removeEventListener("resize", this.onResize);
    if (this.onVisibilityChange) document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (this.onMessagesScroll && this.hasMessagesTarget) this.messagesTarget.removeEventListener("scroll", this.onMessagesScroll);
    if (window.visualViewport && this.onViewportResize) window.visualViewport.removeEventListener("resize", this.onViewportResize);
  }

  onConnected() {
    this.enableInput();
    // Register presence immediately on (re)connect, before the first interval tick.
    this.subscription.perform("heartbeat");
  }

  onDisconnected() {
    this.setInputDisabled(true);
  }

  received(data) {
    switch (data.type) {
      case "backfill":
        this.messagesTarget.replaceChildren();
        (data.messages || []).forEach((message) => this.appendMessage(message));
        this.scrollToBottom();
        break;
      case "message": {
        // Our own message echoed back always scrolls into view, even if the reader had scrolled
        // up before sending. `pinned` must be computed here, before appendMessage() below changes
        // scrollHeight - isPinnedToBottom() after the append would no longer reflect pre-send state.
        const mine = this.sentClientMsgIds.delete(data.client_msg_id);
        const pinned = mine || this.isPinnedToBottom();
        this.appendMessage(data);
        if (pinned) this.scrollToBottom();
        break;
      }
      case "remove":
        this.removeMessage(data.client_msg_id);
        break;
      case "ban":
        this.handleBan(data.user_id);
        break;
      case "viewers":
        this.updateViewerCount(data.count);
        break;
      case "rejected":
        if (data.reason === "slow_mode") this.handleSlowMode();
        break;
      case "announcement":
        this.setAnnouncement(data.body, data.body_html);
        break;
      case "poll":
        // Forward poll events to the floating card (window event so the card may be absent).
        this.dispatch("poll", { detail: data, target: window });
        break;
      case "tip":
        // A tip renders in BOTH places (deduped by id): highlighted inline AND in the pinned
        // carousel. The carousel may be absent (dashboard / flag off), so forward via a window event.
        if (data.action === "created") {
          this.addInlineTip(data.tip);
          this.dispatch("tip", { detail: data.tip, target: window });
        } else if (data.action === "removed") {
          const id = data.tip && data.tip.id;
          this.removeInlineTip(id);
          this.dispatch("tip-removed", { detail: { id }, target: window });
        }
        break;
      case "tips_backfill":
        // Subscribe-time backfill (latest-first) for the carousel only; the inline scroll is
        // rebuilt from the chat backfill and does not replay past tips.
        this.dispatch("tips-backfill", { detail: data.tips || [], target: window });
        break;
    }
  }

  // Count renders twice (mobile span + desktop pill, both outside this controller's element) -
  // a single querySelector only ever hit the first (mobile) node, freezing the other viewport's.
  updateViewerCount(count) {
    document.querySelectorAll("[data-live-viewer-count]").forEach((el) => {
      el.setAttribute("data-rolling-number-count-value", count);
    });
  }

  // Cast a vote for the floating card over our shared subscription (it has none of its own).
  castVote(event) {
    if (!this.subscription) return;

    this.subscription.perform("vote", {
      poll_id: event.detail.pollId,
      option_id: event.detail.optionId,
    });
  }

  setAnnouncement(body, bodyHtml) {
    if (!this.hasAnnouncementTarget) return;

    const text = body || "";
    // body_html is server-escaped + linkified, so it's safe to inject; fall back to plain text if an older payload omits it.
    if (bodyHtml != null) {
      this.announcementBodyTarget.innerHTML = bodyHtml;
    } else {
      this.announcementBodyTarget.textContent = text;
    }
    this.announcementTarget.classList.toggle("hidden", text.trim() === "");
    this.collapseAnnouncement();
    this.scheduleAnnouncementToggleRefresh();
  }

  toggleAnnouncement() {
    this.announcementExpanded = !this.announcementExpanded;
    this.announcementTextTarget.classList.toggle("line-clamp-2", !this.announcementExpanded);
    this.announcementToggleTarget.classList.toggle("rotate-180", this.announcementExpanded);
    this.announcementToggleTarget.setAttribute("aria-expanded", String(this.announcementExpanded));
  }

  collapseAnnouncement() {
    this.announcementExpanded = false;
    if (this.hasAnnouncementTextTarget) this.announcementTextTarget.classList.add("line-clamp-2");
    if (this.hasAnnouncementToggleTarget) {
      this.announcementToggleTarget.classList.remove("rotate-180");
      this.announcementToggleTarget.setAttribute("aria-expanded", "false");
    }
  }

  scheduleAnnouncementToggleRefresh() {
    window.requestAnimationFrame(() => this.refreshAnnouncementToggle());
  }

  // Show the expand chevron only when the clamped text overflows two lines. While
  // expanded the chevron stays (it's the collapse control), so only measure when collapsed.
  refreshAnnouncementToggle() {
    if (!this.hasAnnouncementToggleTarget || !this.hasAnnouncementTextTarget) return;
    if (this.announcementExpanded) return;

    const el = this.announcementTextTarget;
    const overflowing = el.scrollHeight > el.clientHeight + 1;
    this.announcementToggleTarget.classList.toggle("hidden", !overflowing);
  }

  send(event) {
    event.preventDefault();

    const body = this.inputTarget.value.trim();
    if (body === "") return;

    // Generated once: the same id is tracked in sentClientMsgIds (so received() can recognize
    // our own echo) and sent as client_msg_id, so they must never drift apart.
    const clientMsgId = this.newClientMsgId();
    this.sentClientMsgIds.add(clientMsgId);
    this.subscription.perform("speak", { body, client_msg_id: clientMsgId });
    this.inputTarget.value = "";
    this.focusInput();
  }

  // Keeps the on-screen keyboard open on mobile across a send, since blurring the input closes it.
  focusInput() {
    if (this.hasInputTarget && !this.inputTarget.disabled) this.inputTarget.focus();
  }

  deleteMessage(event) {
    if (!this.moderatorValue) return;

    this.subscription.perform("delete_message", { client_msg_id: event.params.clientMsgId });
  }

  banUser(event) {
    if (!this.moderatorValue) return;
    if (!window.confirm("Remove this person from chat? Their messages will be deleted.")) return;

    this.subscription.perform("ban_user", { user_id: event.params.userId });
  }

  // Moderator remove from the inline tip's toolbar (params-based, like deleteMessage). The server
  // unpins + removes it for everyone; no refund.
  removeTip(event) {
    this.performRemoveTip(event.params.tipId);
  }

  // Moderator remove from the pinned carousel. The carousel owns no subscription, so it asks us
  // (which does) via a window event.
  removeCarouselTip(event) {
    this.performRemoveTip(event.detail && event.detail.id);
  }

  performRemoveTip(tipId) {
    if (!this.moderatorValue) return;
    if (tipId == null) return;
    // Money is involved and removal is not refunded, so confirm before an accidental click.
    if (!window.confirm("Remove this tip? The payment is not refunded.")) return;

    this.subscription.perform("remove_tip", { id: tipId });
  }

  appendMessage(message) {
    if (this.messagesTarget.querySelector(`[data-client-msg-id="${CSS.escape(message.client_msg_id)}"]`)) return;

    this.messagesTarget.appendChild(this.buildMessage(message));
  }

  // Clones messageTemplate (declared in the panel markup) and fills its slots. Markup lives
  // in the ERB template; this only ever populates it.
  buildMessage(message) {
    const wrapper = this.messageTemplateEl.content.cloneNode(true).firstElementChild;
    wrapper.dataset.clientMsgId = message.client_msg_id;
    wrapper.dataset.userId = message.user.id;

    this.fillAvatar(wrapper, message.user);
    this.fillIdentity(wrapper, message.user.name, !!message.user.is_channel_admin, message.user.id);
    this.fillBody(wrapper.querySelector("[data-chat-message-body]"), message.body, !!message.user.is_moderator);

    if (this.moderatorValue) wrapper.appendChild(this.buildModeratorActions(message));

    return wrapper;
  }

  // Shared by buildMessage and buildTipMessage: shows the real photo when avatar_url is present,
  // otherwise the colored-initials fallback ChatSenderIdentity computed server-side (so it matches
  // AvatarComponent elsewhere in the app).
  fillAvatar(row, user) {
    applyAvatar(
      row.querySelector("[data-chat-message-avatar]"),
      row.querySelector("[data-chat-message-avatar-fallback]"),
      user
    );
  }

  // Shared by buildMessage and buildTipMessage: sets the display name (a new-tab link to the
  // sender's dashboard profile for moderators only) and, for a channel admin, appends the cloned
  // moderator badge into the identity slot already in the row.
  fillIdentity(row, name, isChannelAdmin, userId) {
    const nameEl = row.querySelector("[data-chat-message-name]");
    nameEl.className = `font-semibold ${isChannelAdmin ? "text-accent" : "text-strong-text"}`;

    if (this.moderatorValue && !isChannelAdmin && userId != null) {
      // Channel admins have no subscriber profile (that route 404s), so only link real subscribers.
      // textContent (never innerHTML) keeps the user-controlled display name XSS-safe.
      const link = document.createElement("a");
      link.setAttribute("href", `${this.subscriberProfileBaseUrlValue}/${userId}`);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener");
      link.className = "text-inherit hover:underline";
      link.textContent = name;
      nameEl.replaceChildren(link);
    } else {
      nameEl.textContent = name;
    }

    if (isChannelAdmin) row.querySelector("[data-chat-message-identity]").appendChild(this.buildModeratorBadge());
  }

  // Renders the message body. For moderators, bare https?:// URLs become new-tab links; everyone
  // else's body is plain text (identical to before). Anchors are built via createElement + textContent
  // (never innerHTML), and only https?:// matches, so pasted javascript:/data:/HTML can never become a link.
  fillBody(bodyEl, body, senderIsModerator) {
    const text = body || "";
    if (!senderIsModerator) {
      bodyEl.textContent = text;
      return;
    }
    bodyEl.replaceChildren();
    const regex = new RegExp(CHAT_URL_REGEX.source, "gi"); // fresh instance: global regex is stateful
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      let url = match[0];
      // Don't swallow trailing sentence punctuation, e.g. "see https://x.com." or "(https://x.com)".
      const trailing = url.match(/[.,;:!?)\]}'">]+$/);
      if (trailing) url = url.slice(0, url.length - trailing[0].length);
      const start = match.index;
      const end = start + url.length;
      if (start > lastIndex) bodyEl.appendChild(document.createTextNode(text.slice(lastIndex, start)));
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer nofollow");
      link.className = "text-accent underline";
      link.textContent = url;
      bodyEl.appendChild(link);
      lastIndex = end;
      regex.lastIndex = end; // resume after the trimmed URL so trailing punctuation is re-emitted as text
    }
    if (lastIndex < text.length) bodyEl.appendChild(document.createTextNode(text.slice(lastIndex)));
  }

  buildModeratorBadge() {
    const badge = document.createElement("span");
    badge.className = "inline-flex items-center";
    badge.setAttribute("role", "img");
    badge.setAttribute("aria-label", "Moderator");
    badge.title = "Moderator";
    badge.appendChild(this.moderatorBadgeTarget.content.cloneNode(true));
    badge.querySelectorAll("svg, img").forEach((el) => el.setAttribute("aria-hidden", "true"));
    return badge;
  }

  // A small icon toolbar that floats over the top-right of the message and only
  // appears on hover, so it never reserves column width in the chat. Hidden +
  // non-interactive until the row is hovered. Icons are cloned from the
  // IconComponent-rendered <template>s in the panel markup; the toolbar shell itself
  // is also cloned from a <template> (moderatorActionsTemplate).
  buildModeratorActions(message) {
    const actions = this.moderatorActionsTemplateEl.content.cloneNode(true).firstElementChild;

    const remove = actions.querySelector("[data-chat-message-delete-button]");
    remove.appendChild(this.deleteIconTarget.content.cloneNode(true));
    remove.dataset.chatPanelClientMsgIdParam = message.client_msg_id;

    const ban = actions.querySelector("[data-chat-message-ban-button]");
    ban.appendChild(this.banIconTarget.content.cloneNode(true));
    ban.dataset.chatPanelUserIdParam = message.user.id;

    return actions;
  }

  removeMessage(clientMsgId) {
    const node = this.messagesTarget.querySelector(`[data-client-msg-id="${CSS.escape(clientMsgId)}"]`);
    if (node) node.remove();
  }

  // Append a tip as a highlighted inline message, deduped by tip id so a redelivered tip
  // (reconnect) never doubles.
  addInlineTip(tip) {
    if (!tip || tip.id == null) return;
    if (this.messagesTarget.querySelector(`[data-tip-id="${CSS.escape(String(tip.id))}"]`)) return;

    const pinned = this.isPinnedToBottom();
    this.messagesTarget.appendChild(this.buildTipMessage(tip));
    if (pinned) this.scrollToBottom();
  }

  // Clones tipMessageTemplate and fills its slots; mirrors buildMessage plus the amount badge.
  buildTipMessage(tip) {
    const wrapper = this.tipMessageTemplateEl.content.cloneNode(true).firstElementChild;
    wrapper.dataset.tipId = tip.id;
    if (tip.user) wrapper.dataset.userId = tip.user.id;

    this.fillAvatar(wrapper, tip.user);
    this.fillIdentity(wrapper, (tip.user && tip.user.name) || "", !!(tip.user && tip.user.is_channel_admin), tip.user && tip.user.id);
    wrapper.querySelector("[data-chat-tip-amount]").textContent = `TIP ${formatMoney(tip.amount_cents, tip.currency)}`;

    const bodyEl = wrapper.querySelector("[data-chat-message-body]");
    const body = (tip.body || "").trim();
    if (body !== "") {
      bodyEl.textContent = body;
    } else {
      bodyEl.remove();
    }

    if (this.moderatorValue) wrapper.appendChild(this.buildTipModeratorActions(tip));

    return wrapper;
  }

  // A hover-revealed remove control for the inline tip, mirroring buildModeratorActions with a
  // single action. Reuses the trash icon <template>; the toolbar shell is cloned from
  // tipModeratorActionsTemplate.
  buildTipModeratorActions(tip) {
    const actions = this.tipModeratorActionsTemplateEl.content.cloneNode(true).firstElementChild;

    const remove = actions.querySelector("[data-chat-message-remove-tip-button]");
    remove.appendChild(this.deleteIconTarget.content.cloneNode(true));
    remove.dataset.chatPanelTipIdParam = tip.id;

    return actions;
  }

  removeInlineTip(id) {
    if (id == null) return;
    const node = this.messagesTarget.querySelector(`[data-tip-id="${CSS.escape(String(id))}"]`);
    if (node) node.remove();
  }

  handleBan(userId) {
    this.messagesTarget
      .querySelectorAll(`[data-user-id="${CSS.escape(String(userId))}"]`)
      .forEach((node) => node.remove());

    if (String(userId) === String(this.currentUserIdValue)) {
      this.banned = true;
      this.setInputDisabled(true);
      this.bannedTarget.classList.remove("hidden");
    }
  }

  handleSlowMode() {
    this.enableInput();
    this.hintTarget.textContent = "Slow mode is on — please wait a moment before sending another message.";
    this.hintTarget.classList.remove("hidden");

    if (this.hintTimeout) window.clearTimeout(this.hintTimeout);
    this.hintTimeout = window.setTimeout(() => this.hintTarget.classList.add("hidden"), 4000);
  }

  enableInput() {
    if (this.banned) return;
    this.setInputDisabled(false);
  }

  setInputDisabled(disabled) {
    this.inputTarget.disabled = disabled;
    this.submitTarget.disabled = disabled;
  }

  isPinnedToBottom() {
    const el = this.messagesTarget;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }

  newClientMsgId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return `m-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
